import { Player } from './player.js';
import { AudioOutput } from './audio.js';

export function mountPlayer(root, configUrl) {
  const listeners = new AbortController();
  const element = id => root.getElementById(id);
  let closed = false;
  let player = null, audio = null, config = null, busy = false, ready = false;
  let initializationError = null;
  let zoom = 'fit';
  const status = (text, error = false) => { element('status').textContent = text; element('status').classList.toggle('error', error); };
  const inputLabels = { next: 'Next Nav', sinclair: 'Sinclair', cursor: 'Cursor', kempston: 'Kempston' };
  function startLabel(text) { element('start').title = text; element('start').setAttribute('aria-label', text); }
  function controls() {
    element('machine').disabled = busy;
    element('start').disabled = busy || !config;
    element('pause').disabled = busy || !ready;
    element('reset').disabled = busy || !ready;
    const pauseLabel = player?.running ? 'Pause' : 'Resume';
    element('pause').title = pauseLabel;
    element('pause').setAttribute('aria-label', pauseLabel);
    element('pause').querySelector('.icon').className = `icon ${player?.running ? 'pause' : 'play'}`;
    const machine = element('machine').value;
    const frame = root.querySelector('.desktop-window');
    if (zoom === 'fit') {
      delete frame.dataset.zoom;
      frame.style.removeProperty('--display-width');
      frame.style.removeProperty('--display-height');
      root.host.style.removeProperty('width');
      root.host.style.removeProperty('height');
    } else {
      frame.dataset.zoom = zoom;
      const [width, height] = machine === 'next' ? [320, 256] : [352, 296];
      frame.style.setProperty('--display-width', `${width * Number(zoom)}px`);
      frame.style.setProperty('--display-height', `${height * Number(zoom)}px`);
      root.host.style.width = `${width * Number(zoom) + 26}px`;
      root.host.style.height = 'auto';
    }
    for (const button of root.querySelectorAll('button[data-zoom]')) button.setAttribute('aria-pressed', String(button.dataset.zoom === zoom));
    const name = element('machine').selectedOptions[0].textContent;
    element('window-title').textContent = `Bizmuth — ${name}`;
    element('machine-status').textContent = name;
    element('input-status').textContent = inputLabels[element('arrows').value];
    element('playback-status').textContent = busy ? 'Loading…' : ready ? player?.running ? 'Running' : 'Paused' : 'Stopped';
    element('sd-status').hidden = !(ready && machine === 'next');
    element('sd-state').textContent = ready && machine === 'next' ? 'Supplied image' : 'Not attached';
    for (const button of root.querySelectorAll('[data-action]')) button.disabled = element(button.dataset.action).disabled;
    for (const button of root.querySelectorAll('[data-machine]')) {
      button.disabled = busy;
      button.setAttribute('aria-pressed', String(button.dataset.machine === machine));
    }
    for (const button of root.querySelectorAll('[data-arrows]')) button.setAttribute('aria-pressed', String(button.dataset.arrows === element('arrows').value));
  }
  async function dispose() {
    ready = false;
    const currentAudio = audio, currentPlayer = player;
    player = audio = null;
    await currentAudio?.dispose();
    await currentPlayer?.dispose();
  }
  async function action(operation, rejectErrors = false) {
    if (closed || busy) {
      if (rejectErrors) throw new Error(closed ? 'Player is disconnected' : 'Player is busy');
      return;
    }
    busy = true; controls();
    try { await operation(); }
    catch (error) {
      if (!closed) status(`${error.message} Check the connection or asset settings, then start again.`, true);
      await dispose();
      if (rejectErrors) throw error;
    } finally { busy = false; if (!closed) { element('progress').hidden = true; controls(); } }
  }
  async function start(machine) {
    if (![...element('machine').options].some(option => option.value === machine)) throw new Error(`Unknown machine: ${machine}`);
    await initialized;
    if (!config) throw initializationError ?? new Error('Player configuration is unavailable');
    return action(async () => {
      element('machine').value = machine;
      controls();
      await dispose();
      if (closed) throw new Error('Player is disconnected');
      status('Preparing your Spectrum…');
      player = new Player(element('screen'));
      audio = new AudioOutput(player);
      audio.mute(element('mute').getAttribute('aria-pressed') === 'true');
      const rate = await audio.activate();
      if (closed) throw new Error('Player is disconnected');
      player.addEventListener('progress', ({ detail }) => {
        const progress = element('progress');
        progress.hidden = false;
        if (detail.total) { progress.max = detail.total; progress.value = detail.received; }
        else progress.removeAttribute('value');
        status(`Downloading ${detail.label}: ${(detail.received / 1048576).toFixed(1)} MB${detail.total ? ` of ${(detail.total / 1048576).toFixed(1)} MB` : ''}`);
      });
      player.addEventListener('error', ({ detail }) => {
        status(`Emulation stopped: ${detail.error}. Start again to retry.`, true);
        action(dispose);
      });
      const suspended = () => {
        status('Paused while the browser is inactive. Resume to continue.');
        controls();
        audio.pause().catch(() => {});
      };
      player.addEventListener('suspended', suspended);
      audio.addEventListener('suspended', suspended);
      await player.load(machine, config, rate);
      if (closed) throw new Error('Player is disconnected');
      element('arrows').value = machine === 'next' ? 'next' : 'sinclair';
      await audio.resume();
      if (closed) throw new Error('Player is disconnected');
      ready = true;
      startLabel('Start again');
      element('screen').focus();
      status('Running. Click the display for keyboard input.');
    }, true);
  }
  element('controls').onsubmit = event => {
    event.preventDefault();
    if (busy || !config) return;
    start(element('machine').value).catch(() => {});
  };
  element('pause').onclick = () => action(async () => {
    if (player.running) { await audio.pause(); status('Paused. Resume to continue.'); }
    else { await audio.resume(); status('Running.'); element('screen').focus(); }
  });
  element('reset').onclick = () => action(async () => { await audio.reset(); status(player.running ? 'Reset. Running.' : 'Reset. Paused.'); });
  element('machine').onchange = () => action(async () => {
    await dispose();
    startLabel('Start');
    status('Machine selected. Start to load a new session.');
  });
  element('arrows').onchange = () => { if (player) { player.release(); player.keyboard.mode = element('arrows').value; } controls(); };
  for (const button of root.querySelectorAll('button[data-zoom]')) button.onclick = () => {
    zoom = button.dataset.zoom;
    controls();
  };
  element('mute').onclick = () => {
    const muted = element('mute').getAttribute('aria-pressed') !== 'true';
    element('mute').setAttribute('aria-pressed', String(muted));
    element('mute').textContent = muted ? 'Unmute' : 'Mute';
    audio?.mute(muted);
  };
  element('fullscreen').onclick = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await element('display').requestFullscreen();
      element('screen').focus();
    } catch (error) { status(`Full screen unavailable: ${error.message}`, true); }
  };
  const menus = [...root.querySelectorAll('.menubar details')];
  function closeMenus() { for (const menu of menus) menu.open = false; }
  for (const menu of menus) menu.addEventListener('toggle', () => {
    if (menu.open) for (const other of menus) if (other !== menu) other.open = false;
  });
  root.addEventListener('click', event => {
    if (!event.target.closest('.menubar') || event.target.closest('.menu button:not(:disabled)')) closeMenus();
  }, { signal: listeners.signal });
  document.addEventListener('click', event => { if (!event.composedPath().includes(root)) closeMenus(); }, { signal: listeners.signal });
  root.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      const open = menus.find(menu => menu.open);
      closeMenus();
      open?.querySelector('summary').focus();
    }
  }, { signal: listeners.signal });
  for (const button of root.querySelectorAll('[data-action]')) button.onclick = () => element(button.dataset.action).click();
  for (const button of root.querySelectorAll('[data-machine]')) button.onclick = () => {
    element('machine').value = button.dataset.machine;
    element('machine').dispatchEvent(new Event('change'));
  };
  for (const button of root.querySelectorAll('[data-arrows]')) button.onclick = () => {
    element('arrows').value = button.dataset.arrows;
    element('arrows').dispatchEvent(new Event('change'));
  };
  for (const [control, target] of [['toggle-toolbar', 'controls'], ['toggle-statusbar', 'statusbar']]) element(control).onclick = () => {
    const hidden = !element(target).hidden;
    element(target).hidden = hidden;
    element(control).setAttribute('aria-pressed', String(!hidden));
  };
  element('show-help').onclick = () => element('help-dialog').showModal();
  element('show-about').onclick = () => element('about-dialog').showModal();
  window.addEventListener('pagehide', () => { dispose().catch(() => {}); }, { signal: listeners.signal });
  const initialized = (async () => {
  try {
    const response = await fetch(configUrl, { cache: 'no-store', signal: listeners.signal });
    if (!response.ok) throw new Error(`Configuration download failed: HTTP ${response.status}`);
    config = await response.json();
    if (closed) return;
    for (const key of ['romZipUrl', 'sdZipUrl']) if (config[key]) config[key] = new URL(config[key], response.url).href;
    if ([...element('machine').options].some(option => option.value === config.defaultMachine)) element('machine').value = config.defaultMachine;
  } catch (error) {
    initializationError = error;
    config = null;
    if (!closed) status(`${error.message}. Reload to retry.`, true);
  }
  if (!closed) controls();
  })();
  return {
    ready: initialized,
    start,
    async dispose() { closed = true; listeners.abort(); await dispose(); },
  };
}
